<modal-dialog #editGroupModal [dialogTemplate]="editGroupDialogTemplate"></modal-dialog>
<ng-template #editGroupDialogTemplate>
  <div
    class="light-content-panel light-control-host"
    (keydown.escape)="close()"
    tabindex="0"
    arpSurfaceErrorHost="edit-group-modal"
    #dialogError="surfaceErrorHost"
  >
    <div class="modal-header">
      <div class="modal-header-left">
        <h2 class="panel-title margin-left">Edit Group</h2>
        <a class="delete-group-button margin-left" (click)="deleteGroup()">Delete Group...</a>
      </div>
      <button type="button" class="modal-close-btn" (click)="close()">
        <svg lucideX></svg>
      </button>
    </div>
    <form [formGroup]="groupForm" (ngSubmit)="onSave()" (keydown.enter)="$event.preventDefault()">
      <div class="modal-body">
        <!-- Group Information Section -->
        <div class="form-section">
          <h3>Group Information</h3>
          <div class="role-information-body">
            <div class="flex-row gap-4">
              <div class="form-row flex-1">
                <label class="form-label">Group Name</label>
                <input
                  type="text"
                  formControlName="name"
                  class="arpio-input full-width"
                  placeholder="Enter group name"
                />
                <arp-field-issue [message]="nameErrors"></arp-field-issue>
              </div>

              <div class="form-row flex-1">
                <label class="form-label"
                  >External ID
                  <span class="input-help-text text-xs"
                    >(Optional - Used to map this group to external identity providers.)</span
                  ></label
                >
                <input
                  type="text"
                  formControlName="externalId"
                  class="arpio-input full-width"
                  placeholder="External group identifier"
                />
                <arp-field-issue [message]="externalIdErrors"></arp-field-issue>
                <div class="input-help-text text-xs">
                  Can only contain letters, numbers, hyphens, and underscores. Maximum 64 characters.
                </div>
              </div>
            </div>
            <!-- v2 - when api supports descriptions -->
            <!-- <div class="form-group">
              <label class="form-label">Description <span class="label-optional">(Optional)</span></label>
              <textarea
                formControlName="description"
                class="arpio-input full-width"
                placeholder="Describe what this group is used for..."
                rows="2"
              ></textarea>
            </div> -->
          </div>
        </div>

        <!-- Assignments Section -->
        <div class="form-section">
          <h3>Update Group Assignments</h3>
          <div class="flex justify-center full-width create-group-assignments-section" style="gap: 2rem">
            <div class="table-container flex-1">
              <div class="flex-row items-center gap-4 margin-bottom">
                <h4 class="whitespace-nowrap">Assign Roles</h4>
                <div class="filter-container">
                  @if (isModalOpen()) {
                    <input
                      pInputText
                      type="text"
                      #rolesFilter
                      (input)="filterRoles($event)"
                      placeholder="Filter roles..."
                      class="arpio-input full-width filter-input"
                    />
                  }
                </div>
              </div>
              <!-- Assign Roles Table -->
              <div>
                @if (isModalOpen()) {
                  <p-table
                    #rolesTable
                    [value]="availableRoles"
                    [globalFilterFields]="['name', 'accessLevel']"
                    dataKey="id"
                    [scrollable]="true"
                    scrollHeight="300px"
                    [tableStyle]="{ 'min-width': '20rem' }"
                    class="p-datatable-sm"
                    [loading]="loadingRoles"
                    [customSort]="true"
                    (sortFunction)="customSort($event)"
                  >
                    <ng-template #header>
                      <tr>
                        <th class="checkbox-column">
                          <input
                            type="checkbox"
                            class="arpio-checkbox"
                            [checked]="areAllRolesSelected()"
                            [indeterminate]="areSomeRolesSelected()"
                            (change)="toggleAllRoles($event)"
                          />
                        </th>
                        <th pSortableColumn="name" class="role-name-column">
                          Role Name <p-sort-icon field="name"></p-sort-icon>
                        </th>
                        <th pSortableColumn="accessLevel">
                          Access Level(s) <p-sort-icon field="accessLevel"></p-sort-icon>
                        </th>
                      </tr>
                    </ng-template>
                    <ng-template #body let-role>
                      <tr [ngClass]="{ 'default-role-row': isDefaultAccountRole(role) }">
                        <td class="checkbox-column">
                          <input
                            type="checkbox"
                            class="arpio-checkbox"
                            [checked]="isRoleSelected(role)"
                            (change)="toggleRole(role, $event)"
                          />
                        </td>
                        <td class="role-name-column">
                          <div>
                            {{ role.name }}
                          </div>
                        </td>
                        <td>
                          @if (role.accessLevels && role.accessLevels.length > 0) {
                            <div class="access-level-badges">
                              @for (accessLevelInfo of role.accessLevels; track accessLevelInfo) {
                                <p-tag
                                  class="access-level-badge fit-width"
                                  [pTooltip]="getAccessLevelTooltip(accessLevelInfo)"
                                  tabindex="0"
                                  role="note"
                                  [attr.aria-label]="getAccessLevelTooltip(accessLevelInfo)"
                                  tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                                  tooltipPosition="top"
                                  [autoHide]="false"
                                  [hideDelay]="200"
                                  [escape]="false"
                                  tooltipEvent="hover"
                                  value="{{
                                    accessLevelService.getAccessLevelDisplayName(accessLevelInfo.accessLevel)
                                  }}"
                                >
                                </p-tag>
                              }
                            </div>
                          } @else {
                            <div>{{ accessLevelService.getAccessLevelDisplayName(role.accessLevel) }}</div>
                          }
                        </td>
                      </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                      <tr>
                        <td colspan="3" class="empty-state-cell text-center">
                          <div class="empty-state-content">
                            <p class="empty-state-message">No roles available.</p>
                            <p class="empty-state-submessage">
                              Create roles in Role Management to assign them to groups.
                            </p>
                          </div>
                        </td>
                      </tr>
                    </ng-template>
                    <ng-template #summary>
                      @if (!loadingRoles) {
                        <div class="flex align-items-center justify-content-between">
                          <span
                            >Displaying {{ rolesTable.filteredValue?.length ?? rolesTable.value?.length }} of
                            {{ rolesTable.value?.length }} roles</span
                          >
                        </div>
                      }
                    </ng-template>
                  </p-table>
                }
              </div>
            </div>

            <!-- Assign Users Table -->
            <div class="table-container flex-1">
              <div class="flex-row items-center gap-4 margin-bottom">
                <h4 class="whitespace-nowrap">Assign Users</h4>
                <div class="filter-container">
                  @if (isModalOpen()) {
                    <input
                      pInputText
                      type="text"
                      #usersFilter
                      (input)="filterUsers($event)"
                      placeholder="Filter users..."
                      class="arpio-input filter-input"
                    />
                  }
                </div>
              </div>
              @if (isModalOpen()) {
                <p-table
                  #usersTable
                  [value]="availableUsers"
                  [globalFilterFields]="['name', 'email']"
                  dataKey="id"
                  [scrollable]="true"
                  scrollHeight="300px"
                  [tableStyle]="{ 'min-width': '20rem' }"
                  class="p-datatable-sm"
                >
                  <ng-template #header>
                    <tr>
                      <th class="checkbox-column">
                        <input
                          type="checkbox"
                          class="arpio-checkbox"
                          [checked]="areAllUsersSelected()"
                          [indeterminate]="areSomeUsersSelected()"
                          (change)="toggleAllUsers($event)"
                        />
                      </th>
                      <th pSortableColumn="name">User Name <p-sort-icon field="name"></p-sort-icon></th>
                      <th pSortableColumn="email">Email <p-sort-icon field="email"></p-sort-icon></th>
                    </tr>
                  </ng-template>
                  <ng-template #body let-user>
                    <tr [ngClass]="{ 'default-role-row': isCurrentUser(user) }">
                      <td class="checkbox-column">
                        <input
                          type="checkbox"
                          class="arpio-checkbox"
                          [checked]="isUserSelected(user)"
                          (change)="toggleUser(user, $event)"
                        />
                      </td>
                      <td>
                        <div>
                          {{ user.name }}
                          @if (isCurrentUser(user)) {
                            <span> (You)</span>
                          }
                        </div>
                      </td>
                      <td>
                        <div>{{ user.email }}</div>
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template #emptymessage>
                    <tr>
                      <td colspan="3" class="empty-state-cell text-center">
                        <div class="empty-state-content">
                          <p class="empty-state-message">No users available.</p>
                          <p class="empty-state-submessage">Invite users to assign them to groups.</p>
                        </div>
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template #summary>
                    @if (!loadingRoles) {
                      <div class="flex align-items-center justify-content-between">
                        <span
                          >Displaying {{ usersTable.filteredValue?.length ?? usersTable.value?.length }} of
                          {{ usersTable.value?.length }} users</span
                        >
                      </div>
                    }
                  </ng-template>
                </p-table>
              }
            </div>
          </div>
        </div>
      </div>

      @if (dialogError.error) {
        <arp-error-block
          class="dialog-error"
          [error]="dialogError.error"
          [dismissible]="true"
          (dismissed)="dialogError.clear()"
        ></arp-error-block>
      }

      <div class="modal-footer">
        @if (dialogError.error?.blockedReason) {
          <span class="modal-footer-hint">{{ dialogError.error.blockedReason }}</span>
        }

        <button type="button" class="arpio-secondary-button" (click)="close()">Cancel</button>
        <button
          type="submit"
          class="arpio-button"
          [disabled]="!isFormValid || saving || !!dialogError.error?.blockedReason"
        >
          @if (saving) {
            <svg lucideLoaderCircle class="lucide-spin"></svg>
          }
          {{ getSaveButtonText() }}
        </button>
      </div>
    </form>
  </div>
</ng-template>

<!-- Delete Group Confirmation Dialog -->
<modal-dialog #deleteGroupDialog [dialogTemplate]="confirmDeleteGroupDialog"></modal-dialog>
<ng-template #confirmDeleteGroupDialog>
  <confirm-action-dialog
    name="&quot;{{ groupToDelete?.name }}&quot;"
    action="Delete"
    dialogId="edit-group-delete-confirm"
    (onCancel)="cancelDeleteGroup()"
    (onAction)="confirmDeleteGroup()"
  >
    <div class="error margin-top">
      Deleting this group will remove all role and user assignments. This action cannot be undone.
    </div>
  </confirm-action-dialog>
</ng-template>
